import React from 'react';
import { Helmet } from 'react-helmet-async';
import { FormattedMessage, useIntl } from 'react-intl';
import styled, { keyframes } from 'styled-components';

import { Link } from '../StyledComponents';
import { useSsrContext } from '../ssrContext';

const Page = styled.section`
  min-height: 70vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4rem 1.25rem;
`;

const Card = styled.div`
  position: relative;
  width: 100%;
  max-width: 640px;
  background: ${({ theme }) => theme.colors.cardBg};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 16px;
  box-shadow: ${({ theme }) => theme.shadows.elevated};
  padding: 3rem 2.5rem 2.5rem;
  text-align: center;
  overflow: hidden;

  @media (max-width: 576px) {
    padding: 2.5rem 1.5rem 2rem;
    border-radius: 12px;
  }

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    pointer-events: none;
    background-image:
      radial-gradient(${({ theme }) => theme.colors.borderLight} 1px, transparent 1px);
    background-size: 6px 6px;
    opacity: 0.45;
    mask-image: radial-gradient(circle at 50% 0%, black 0%, transparent 65%);
    -webkit-mask-image: radial-gradient(circle at 50% 0%, black 0%, transparent 65%);
  }
`;

const Eyebrow = styled.div`
  font-family: 'Times New Roman', Georgia, serif;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.32em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.sectionAccent};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  padding: 0.4rem 0;
  margin: 0 auto 1.75rem;
  max-width: 320px;
`;

const pressShake = keyframes`
  0%, 100% { transform: rotate(-3deg) translateY(0); }
  25%      { transform: rotate(-1deg) translateY(-2px); }
  75%      { transform: rotate(-5deg) translateY(1px); }
`;

const inkDrip = keyframes`
  0%   { opacity: 0.9; transform: scaleY(1); }
  50%  { opacity: 0.6; transform: scaleY(1.15); }
  100% { opacity: 0.9; transform: scaleY(1); }
`;

const Code = styled.div`
  font-family: 'Times New Roman', Georgia, serif;
  font-weight: 900;
  font-size: clamp(5.5rem, 18vw, 9rem);
  line-height: 0.95;
  letter-spacing: -0.04em;
  color: ${({ theme }) => theme.colors.text};
  margin: 0.25rem 0 0.5rem;
  position: relative;
  display: inline-block;

  &::after {
    content: 'PRESS HALTED';
    position: absolute;
    top: 12%;
    right: -32%;
    transform: rotate(-10deg);
    font-family: 'Times New Roman', Georgia, serif;
    font-size: 0.82rem;
    font-weight: 700;
    letter-spacing: 0.14em;
    color: ${({ theme }) => theme.colors.error};
    border: 2px solid ${({ theme }) => theme.colors.error};
    border-radius: 4px;
    padding: 0.2rem 0.45rem;
    opacity: 0.88;
    animation: ${pressShake} 3.2s ease-in-out infinite;
    pointer-events: none;

    @media (max-width: 576px) {
      right: -8%;
      font-size: 0.68rem;
      letter-spacing: 0.08em;
    }
  }
`;

const InkSmudge = styled.div`
  width: 48px;
  height: 14px;
  margin: 0.5rem auto 0;
  background: ${({ theme }) => theme.colors.textMuted};
  border-radius: 50% 50% 40% 40% / 60% 60% 40% 40%;
  opacity: 0.35;
  animation: ${inkDrip} 2.8s ease-in-out infinite;
`;

const Headline = styled.h1`
  font-family: 'Times New Roman', Georgia, serif;
  font-size: clamp(1.5rem, 4vw, 2rem);
  font-weight: 700;
  line-height: 1.25;
  margin: 0.75rem 0 0.5rem;
  color: ${({ theme }) => theme.colors.text};
`;

const Subheadline = styled.p`
  font-size: 1rem;
  line-height: 1.55;
  color: ${({ theme }) => theme.colors.textSecondary};
  max-width: 460px;
  margin: 0 auto 2rem;
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  justify-content: center;
  position: relative;
  z-index: 1;
`;

const PrimaryAction = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.7rem 1.25rem;
  background: ${({ theme }) => theme.colors.sectionAccent};
  color: ${({ theme }) => theme.colors.textInverse};
  border-radius: 999px;
  font-weight: 600;
  font-size: 0.95rem;
  text-decoration: none;
  transition: filter 0.2s, transform 0.2s;

  &:hover {
    filter: brightness(1.08);
    color: ${({ theme }) => theme.colors.textInverse};
    transform: translateY(-1px);
    text-decoration: none;
  }
`;

const SecondaryAction = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.7rem 1.25rem;
  background: transparent;
  color: ${({ theme }) => theme.colors.text};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 999px;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: background 0.2s, border-color 0.2s, transform 0.2s;

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceHover};
    border-color: ${({ theme }) => theme.colors.sectionAccent};
    transform: translateY(-1px);
  }
`;

const SecondaryLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.7rem 1.25rem;
  background: transparent;
  color: ${({ theme }) => theme.colors.text};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 999px;
  font-weight: 600;
  font-size: 0.95rem;
  text-decoration: none;
  transition: background 0.2s, border-color 0.2s, transform 0.2s;

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceHover};
    border-color: ${({ theme }) => theme.colors.sectionAccent};
    color: ${({ theme }) => theme.colors.text};
    transform: translateY(-1px);
    text-decoration: none;
  }
`;

const Tip = styled.p`
  margin-top: 2rem;
  padding-top: 1.25rem;
  border-top: 1px dashed ${({ theme }) => theme.colors.border};
  font-size: 0.85rem;
  color: ${({ theme }) => theme.colors.textMuted};
  font-style: italic;
`;

const ServerErrorPage: React.FC = () => {
  const intl = useIntl();
  const { context: ssrContext } = useSsrContext();

  if (ssrContext) {
    (ssrContext as { status?: number }).status = 500;
  }

  const title = intl.formatMessage({
    id: 'serverError.metaTitle',
    defaultMessage: '500 — Matbaa arızası',
  });

  return (
    <Page role="main" aria-labelledby="servererror-title">
      <Helmet>
        <title>{title}</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>
      <Card>
        <Eyebrow>
          <FormattedMessage id="serverError.eyebrow" defaultMessage="Acil baskı · Teknik arıza" />
        </Eyebrow>
        <Code aria-hidden="true">500</Code>
        <InkSmudge aria-hidden="true" />
        <Headline id="servererror-title">
          <FormattedMessage
            id="serverError.headline"
            defaultMessage="Matbaa makinesi arızalandı"
          />
        </Headline>
        <Subheadline>
          <FormattedMessage
            id="serverError.subheadline"
            defaultMessage="Baskı bandında beklenmedik bir arıza oluştu — mürekkep döküldü, sayfalar henüz kurumadı. Teknik ekibimiz makineyi tamir ederken lütfen biraz bekleyin."
          />
        </Subheadline>
        <Actions>
          <PrimaryAction to="/">
            <FormattedMessage id="serverError.cta.home" defaultMessage="Ana sayfaya dön" />
          </PrimaryAction>
          <SecondaryAction type="button" onClick={() => window.location.reload()}>
            <FormattedMessage id="serverError.cta.retry" defaultMessage="Tekrar dene" />
          </SecondaryAction>
          <SecondaryLink to="/ara">
            <FormattedMessage id="serverError.cta.search" defaultMessage="Arşivde ara" />
          </SecondaryLink>
        </Actions>
        <Tip>
          <FormattedMessage
            id="serverError.tip"
            defaultMessage="İpucu: Sayfayı yenilemek çoğu geçici arızayı giderir — tıpkı kuruyan mürekkebi ıslatmak gibi."
          />
        </Tip>
      </Card>
    </Page>
  );
};

export default ServerErrorPage;
